import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
import { useOrg } from "./session";

export interface Mailbox {
  id: string;
  provider: "microsoft" | "google";
  email_address: string;
  display_name: string;
  kind: "shared" | "personal";
  owner_member_id: string | null;
  owner_name: string | null;
  can_read: boolean;
  sync_mode: "replies" | "contacts" | "all";
  status: "active" | "needs_reconnect" | "disabled";
  last_synced_at: string | null;
  last_error: string | null;
  ai_agent_id: string | null;
}

export interface EmailOverview {
  accounts: Mailbox[];
  providers: ("microsoft" | "google")[];
  sender_account_id: string | null;
  enabled_in_plan: boolean;
  can_manage: boolean;
  can_connect_shared: boolean;
  sent_this_month: number;
  sent_limit: number | null;
}

export interface SavedEmail {
  id: string;
  name: string;
  subject: string;
  body: string;
  subject_ar: string;
  body_ar: string;
  updated_at: string;
}

export const providerName = { microsoft: "Outlook", google: "Gmail" } as const;

export function useEmail() {
  const { base, orgId } = useOrg();
  return useQuery({ queryKey: [orgId, "email"], queryFn: () => api<EmailOverview>(`${base}/email`), staleTime: 15_000 });
}

export function useSavedEmails(enabled = true) {
  const { base, orgId } = useOrg();
  return useQuery({ queryKey: [orgId, "email-templates"], queryFn: () => api<{ templates: SavedEmail[] }>(`${base}/email/templates`), enabled, staleTime: 30_000 });
}

// Mailboxes the signed-in person can send from.
export function sendable(ov: EmailOverview | undefined, memberId: string | undefined): Mailbox[] {
  return (ov?.accounts ?? []).filter((a) => a.status === "active" && (a.kind === "shared" || a.owner_member_id === memberId));
}
